/**
 * MapUsagePage — 86baefu3r. Per-tenant Mapbox map-load counts for the cost console.
 *
 * Minimal by design: a month picker, three summary figures, and a table. No enforcement,
 * no thresholds, no alerting — those are explicitly out of scope for this ticket, and the
 * page says so rather than leaving a reader to wonder whether a high number did anything.
 *
 * Two honesty rules the UI has to carry, because the underlying data has a gap the numbers
 * alone cannot express:
 *
 *   1. A tenant with no counter row shows an em dash and "no data", NOT "0". A measured
 *      zero and the absence of a measurement are different facts; printing 0 for both is
 *      how a reader concludes a tenant stopped using maps when in truth nothing reported.
 *   2. The counter started at deploy. Any month before that is empty because nothing was
 *      recording, not because nothing happened — so an all-zero month carries an explicit
 *      note instead of being presented as a finding.
 */
import * as React from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Pill, StatusBadge, fieldStyle, labelStyle, subText } from '../../ui/bits';
import { useMapUsage, recentMonths, type MapUsageTenant } from './hooks';
import { apiErrorText } from '@lodex-pro/web-shared';
import { formatNumber } from '@lodex-pro/shared/number-format';

const MONTHS_OFFERED = 12;

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div
      style={{
        flex: '1 1 160px',
        padding: '14px 16px',
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {hint !== undefined && <div style={subText}>{hint}</div>}
    </div>
  );
}

function LoadCell({ t }: { t: MapUsageTenant }): React.JSX.Element {
  if (!t.hasData) {
    // Em dash + explicit pill. See honesty rule 1 in the header — never render 0 here.
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
        <Pill label="NO DATA" tone="warning" />
      </span>
    );
  }
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {t.loadCount.toLocaleString()}
    </span>
  );
}

export function MapUsagePage(): React.JSX.Element {
  const months = React.useMemo(() => recentMonths(MONTHS_OFFERED), []);
  // null = "current month", so the server resolves it and the client never computes a
  // period the server might bucket differently.
  const [month, setMonth] = React.useState<string | null>(null);
  const { data, isPending, isError, error } = useMapUsage(month);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Mapbox usage</h1>
        <p style={{ ...subText, marginTop: '4px', maxWidth: '70ch' }}>
          Map loads per hauler tenant. Mapbox bills per map load — one map instantiation — and
          web-dispatch creates one map per session, so a count is roughly the number of dispatcher
          sessions that opened a map. Reporting only, no limits or alerts.
        </p>
      </div>

      <div style={{ maxWidth: '240px' }}>
        <label htmlFor="map-usage-month" style={labelStyle}>
          Billing month
        </label>
        <select
          id="map-usage-month"
          style={fieldStyle}
          value={month ?? ''}
          onChange={(e): void => {
            setMonth(e.target.value === '' ? null : e.target.value);
          }}
        >
          <option value="">Current month</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {isPending && <div style={subText}>Loading usage…</div>}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          Couldn’t load map usage. {apiErrorText(error, '')}
        </div>
      )}

      {data !== undefined && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <SummaryCard
              label="Total map loads"
              value={data.totalLoads.toLocaleString()}
              hint={data.monthLabel}
            />
            <SummaryCard
              label="Tenants reporting"
              // Both halves shown: "3" alone cannot be read without the denominator, and
              // the gap between them is the interesting number.
              value={`${formatNumber(data.reportingTenants)} / ${formatNumber(data.tenantCount)}`}
              hint="haulers with at least one recorded load"
            />
            <SummaryCard
              label="Billing month"
              value={data.monthLabel}
              hint={`period ${data.month} (UTC)`}
            />
          </div>

          {data.totalLoads === 0 && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '12px',
                backgroundColor: 'var(--color-bg-tertiary)',
                ...subText,
              }}
            >
              No loads recorded for {data.monthLabel}. Counting began when this feature deployed, so
              an earlier month is empty because nothing was recording — not because no maps were
              opened.
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Abbrev</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Map loads</TableHead>
                <TableHead>Last load</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.tenants.map((t) => (
                <TableRow key={t.tenantId}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    {t.haulerAbbreviation ?? <span style={subText}>not set</span>}
                  </TableCell>
                  <TableCell>
                    <StatusBadge active={t.isActive} />
                  </TableCell>
                  <TableCell>
                    <LoadCell t={t} />
                  </TableCell>
                  <TableCell>
                    {/* Local-by-context per DEC-004 — the stored instant is UTC, never shown as one. */}
                    {t.lastLoadedAt === null ? (
                      <span style={subText}>—</span>
                    ) : (
                      new Date(t.lastLoadedAt).toLocaleString()
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}
